// ...existing code...
import { NavLink } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { isDevelopmentMode } from '../contexts/SidebarContext';
import { useRoleAccess } from '../features/users/hooks/useRoleAccess';
import { useAuth } from '../features/authentication/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { Icons } from '@cleanpro/common-ui';

export default function Sidebar() {
  const { isSidebarOpen, closeSidebar, isMobile } = useSidebar();
  const { user } = useAuth();
  const permissions = useRoleAccess();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Icons.BarChart3 },
    { name: 'Schedule', href: '/schedule', icon: Icons.Calendar },
    { type: 'divider' },
    { name: 'Requests', href: '/requests', icon: Icons.Funnel },
    { name: 'Quotes', href: '/quotes', icon: Icons.Receipt },
    { name: 'Jobs', href: '/jobs', icon: Icons.ClipboardCheck },
    // { name: 'Invoices', href: '/invoices', icon: Icons.CreditCard },
    { type: 'divider' },
    { name: 'Customers', href: '/customers', icon: Icons.Users },
    { name: 'Properties', href: '/properties', icon: Icons.Home, permission: permissions.canViewProperties },
    { name: 'Service Providers', href: '/service-providers', icon: Icons.Building2, permission: permissions.canViewEmployees },
    { type: 'divider' },
    { name: 'Services', href: '/services', icon: Icons.Package },
    { type: 'divider' },
    { name: 'Growth Ops', href: '/growth-ops', icon: Icons.TrendingUp },
    { name: 'Audit Log', href: '/growth-ops/audit', icon: Icons.ClipboardCheck },
    { type: 'divider' },
    { name: 'Employees', href: '/employees', icon: Icons.IdCardLanyard },
    { name: 'Settings', href: '/settings', icon: Icons.Cog, permission: permissions.canViewSettings },
    { name: 'Users', href: '/users', icon: Icons.Users, permission: permissions.canViewUserManagement },
    { name: 'Permissions', href: '/adminrbac', icon: Icons.Settings, permission: permissions.canViewPermissions }
  ];

  // Handle click on navigation items - close sidebar on mobile
  const handleNavClick = () => {
    if (isMobile) {
      closeSidebar();
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && isSidebarOpen && (
        <button 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden border-0 p-0 cursor-default"
          onClick={closeSidebar}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              closeSidebar();
            }
          }}
          aria-label="Close sidebar"
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        bg-white min-h-screen shadow-lg flex flex-col border-r border-gray-100 transition-all duration-300 ease-in-out
        ${isMobile ? 'fixed z-50' : 'relative'}
        ${isMobile && !isSidebarOpen ? '-translate-x-full' : 'translate-x-0'}
        ${isMobile ? 'w-64' : 'w-64'}
      `}>
        {/* Mobile close button */}
        {isMobile && (
          <div className="flex justify-end p-4">
            <button
              onClick={closeSidebar}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <Icons.X className="h-6 w-6" />
            </button>
          </div>
        )}

        {/* Brand */}
        <div className={`${isMobile ? 'px-8 pb-6' : 'p-8'} border-b border-gray-100`}>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
              <Icons.Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Go Sit Back</h1>
              <p className="text-sm text-gray-500 mt-1">Management System</p>        
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 p-4">
          <nav className="space-y-1">
            {navigation.filter(item => item.permission !== false).map((item, index) => {
              // Render divider
              if (item.type === 'divider') {
                return (
                  <div key={`divider-${index}`} className="my-3">
                    <div className="border-t border-gray-200"></div>
                  </div>
                );
              }
              
              // Render navigation item
              const IconComponent = item.icon;
              if (!IconComponent) return null;
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href!}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                >
                  <IconComponent
                    className={`mr-3 h-5 w-5 flex-shrink-0`}
                    aria-hidden="true"
                  />
                  {item.name}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Help */}
        <div className="p-6 border-t border-gray-100">
          {/* User Info & Account Management */}
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="mb-3">
                <UserButton />
              </div>
              <p className="text-gray-500 capitalize truncate">
                <button
                  onClick={() => navigate('/profile')}
                  title={`Profile: ${user?.name}`}
                  className="hover:text-gray-700"
                >
                  Profile
                </button>
              </p>
              {isDevelopmentMode() && (
                <p className="text-red-500 capitalize truncate">Development Mode</p>
              )}   
              <p className="text-gray-500 capitalize truncate"><a href="#">Get Help</a></p>
              <p className="text-gray-500 capitalize truncate"><a href="#">What's New?</a></p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}